import { SessionModel } from '@/models/auth/session';
import { authSerializer } from '@/providers/serializers/auth/auth';
import { NAVIGATION_MENU, NAVIGATION_MENU_ROSTER, PERMISSION_CHECK, USER_TYPE } from '@/utils/constants';

export class SessionService {

  static get instance() {
    return this.INSTANCE;
  }

  private static INSTANCE = new SessionService();

  /**
   * Maintains the Demo user session copy
   */
  public DEMO_SESSION: string = 'MC_DEMO_SESSION';

  /**
   * Maintains the mission control session
   */
  private SESSION: string = 'MC_SESSION';

  /**
   * Maintains the demo user from RGO
   */
  private MC_UPDATE: string = 'MC_UPDATE_LOGIN';

  private GROUP_TENANTS_KEY = 'GROUP_HIERARCHY_TENANTS';

  private permissions: any = PERMISSION_CHECK;

  /**
   * Maintains the RGO mission control session.
   */
  private SESSION_RGO: string = 'ember_simple_auth-research-session';

  private APP_USER_GROUP_LEVEL: string = 'MC_APP_USER_GROUP_LEVEL';

  private ACTIVE_TENANT: string = 'activeTenant';


  public getSession() {
    let session = null;
    const sessionAsString: string | null = localStorage.getItem(this.SESSION);
    if (sessionAsString) {
      const sessionAsJson = JSON.parse(sessionAsString);
      session = authSerializer.sessionModelSerializer(sessionAsJson);
    }
    return session;
  }

  public setSession(session: SessionModel) {
    localStorage.setItem(this.SESSION, JSON.stringify(session));
    localStorage.setItem(this.SESSION_RGO, JSON.stringify(authSerializer.sessionModelRGOSerializer(session)));
  }

  public setDemoSessionCopy() {
    const session: any = JSON.stringify(this.getSession());
    localStorage.setItem(this.DEMO_SESSION, session);
  }

  public getDemoSessionCopy() {
    const demoSession: any = localStorage.getItem(this.DEMO_SESSION);
    const session: any = JSON.parse(demoSession);
    return session;
  }

  public deleteSession() {
    localStorage.removeItem(this.SESSION);
    localStorage.removeItem(this.SESSION_RGO);
    localStorage.removeItem(this.DEMO_SESSION);
    localStorage.removeItem(this.APP_USER_GROUP_LEVEL);
    localStorage.removeItem(this.GROUP_TENANTS_KEY);
  }

  /**
   * We have to remove this logic when we moved all the page into vue js
   */
  public getMcUpdate() {
    const login: any = localStorage.getItem(this.MC_UPDATE);
    return JSON.parse(login);
  }

  public deleteMcUpdate() {
    localStorage.removeItem(this.MC_UPDATE);
  }

  public isAuthorized() {
    const session = this.getSession();
    return (session && session.access_token);
  }

  public getCdnUrl() {
    const service = this;
    const session = service.getSession();
    return session ? session.cdn_urls : null;
  }

  public getTenantLogo() {
    const session = this.getSession();
    const tenantSettings = session!.tenant!.settings;
    const logo = tenantSettings.ui_element_visibility_settings &&
      tenantSettings.ui_element_visibility_settings.logo_url &&
      tenantSettings.ui_element_visibility_settings.show_logo ?
      tenantSettings.ui_element_visibility_settings.logo_url : null;
    return logo;
  }

  public getTenantDefaultPassword() {
    const session = sessionService.getSession();
    const tenantSettings = session!.tenant!.settings;
    const pw = tenantSettings.default_password ?
      tenantSettings.default_password : null;
    return pw;
  }

  public saveUpdatedTenantSettings(updatedTenantSettings: any) {
    const session = this.getSession();
    session!.tenant!.settings = updatedTenantSettings;
    localStorage.setItem(this.SESSION, JSON.stringify(session));
  }

  public isSuperAdminUser() {
    const session = this.getSession();
    return session!.isSuperAdmin;
  }

  public viewOnlyRoster() {
    const session = this.getSession();
    return session!.permissions.includes('VIEW_ROSTER');
  }

  public isAdminORSuperAdmin() {
    const session = this.getSession();
    return session!.permissions.includes('GROUP_SUPER_ADMIN')
      || session!.permissions.includes('GROUP_ADMIN');
  }

  public headerMenuOnPermission() {
    let navMenu = NAVIGATION_MENU;
    if (!this.isHavingPermisson(this.permissions.viewSetup)) {
      navMenu = this.findAndRemoveItem(navMenu, 'setup');
    }
    if (!this.isHavingPermisson(this.permissions.showRecruiterMenu)) {
      navMenu = this.findAndRemoveItem(navMenu, 'recruiters');
    }
    if (!this.isHavingPermisson(this.permissions.viewLearningRegistry)) {
      navMenu = this.findAndRemoveItem(navMenu, 'learn-store');
    }
    if (!this.isHavingPermisson(this.permissions.viewDataLake)) {
      navMenu = this.findAndRemoveItem(navMenu, 'data-lake');
    }
    if (!this.isHavingPermisson(this.permissions.viewLearningRegistry)) {
      navMenu = this.findAndRemoveItem(navMenu, 'partner-catalog');
    }
    if (!this.isHavingPermisson(this.permissions.viewRosterOnly) &&
      !this.isHavingPermisson(this.permissions.rosterMobileView)) {
      navMenu = this.findAndRemoveItem(navMenu, 'users-rosters');
    }
    if (!this.isHavingPermisson(this.permissions.GENERATE_ACCESS_CODE)) {
      navMenu = this.findAndRemoveItem(navMenu, 'my-gooru');
    }
    if (!this.isHavingPermisson(this.permissions.USER_REGISTER_APPROVE)) {
      navMenu = this.findAndRemoveItem(navMenu, 'approval-requests');
    }
    if (!this.isHavingPermisson(this.permissions.independentStudy)) {
      navMenu = this.findAndRemoveItem(navMenu, 'independent-study');
    }
    if (!this.isHavingPermisson(this.permissions.communityHub)) {
      navMenu = this.findAndRemoveItem(navMenu, 'community-hub');
    }
    return this.partnerRegisryCheckForSuperAdmin(navMenu);
  }

  public getUserType() {
    let userType;
    if (this.getSession()!.permissions.indexOf('ALL_ACCESS') !== -1) {
      userType = USER_TYPE.superUser;
    } else if (this.getSession()!.permissions.indexOf('VIEW_LR_AS_DU') !== -1) {
      userType = USER_TYPE.districtUser;
    } else if (this.getSession()!.permissions.indexOf('VIEW_LR_AS_CP') !== -1) {
      userType = USER_TYPE.contentProvider;
    }
    return userType;
  }

  private isHavingPermisson(permissionKey: any) {
    const session = this.getSession();
    return session!.permissions.includes(permissionKey);
  }

  private findAndRemoveItem(list: any, itemKey: string) {
    const itemIndex = list.findIndex((item: any) => item.key === itemKey);
    if (itemIndex !== -1) {
      list.splice(itemIndex, 1);
    }
    return list;
  }

  private partnerRegisryCheckForSuperAdmin(navMenu: any) {
    if (this.getUserType() !== USER_TYPE.superUser) {
      const newMenuIndex = navMenu.findIndex((menuItem: any) => menuItem.key === 'learn-store');
      if (newMenuIndex !== -1) {
        const subMenu: any = navMenu[newMenuIndex].subMenu;
        const sbMenuIndex = subMenu.findIndex((sbIndex: any) => sbIndex.key === 'partner-registry');
        if (sbMenuIndex !== -1 && navMenu[newMenuIndex].subMenu) {
          const item: any = navMenu[newMenuIndex].subMenu;
          item.splice(sbMenuIndex, 1);
        }
      }
    }
    return navMenu;
  }
}

export const sessionService = SessionService.instance;
